import React from 'react';
import styled from 'styled-components';

interface Props {
	onConfirm: () => void;
	onCancel: () => void;
}

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Backdrop = styled.div`
	position: absolute;
	inset: 0;
	background: rgb(0 0 0 / 0.4);
`;

const Dialog = styled.div`
	position: relative;
	z-index: 10;
	width: 100%;
	max-width: 24rem;
	border-radius: 8px;
	background: #ffffff;
	padding: 24px;
	box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
`;

const Heading = styled.h2`
	margin: 0 0 8px;
	font-size: 16px;
	line-height: 24px;
	font-weight: 500;
	color: #0f172a;
`;

const Body = styled.p`
	margin: 0 0 16px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #475569;
`;

const Actions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 8px;
`;

const BaseButton = styled.button`
	border: 0;
	border-radius: 4px;
	padding: 6px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	cursor: pointer;
`;

const CancelButton = styled(BaseButton)`
	background: transparent;
	color: #475569;
	&:hover {
		background: #f1f5f9;
	}
`;

const ConfirmButton = styled(BaseButton)`
	background: ${p => p.theme.ticketing.color.interactivePrimary};
	color: #ffffff;
	&:hover {
		opacity: 0.9;
	}
`;

/**
 * Asked before an AI draft overwrites text the agent already typed. Revert can
 * restore it afterwards, but the confirmation keeps the overwrite from being a
 * surprise.
 */
export function ConfirmReplaceWithDraft({onConfirm, onCancel}: Props) {
	return (
		<Overlay>
			<Backdrop data-testid="modal-backdrop" onClick={onCancel} />
			<Dialog role="dialog" aria-labelledby="confirm-replace-draft-heading">
				<Heading id="confirm-replace-draft-heading">Replace what you have written?</Heading>
				<Body>
					Drafting with AI will replace the text currently in the editor. You can use Revert afterwards to get it
					back.
				</Body>
				<Actions>
					<CancelButton type="button" onClick={onCancel}>
						Cancel
					</CancelButton>
					<ConfirmButton type="button" onClick={onConfirm}>
						Replace
					</ConfirmButton>
				</Actions>
			</Dialog>
		</Overlay>
	);
}
